import type { Cycle } from "@src/packages/care/components/frozen-storage-data";

/**
 * Per-form-step required-field checks for the AddSpecimenModal. Each entry
 * returns the translation-key stem for the label of a field that is still
 * empty on this step, so the modal can both disable the Next button and
 * show the patient exactly what is missing (rather than letting her page
 * through, land on Save, and hit an opaque "invalid" gate).
 *
 * The step ids match those declared in AddSpecimenModal.tsx's `formSteps`
 * memo. Unknown steps return an empty list.
 */
export type EmbryoFormStep = "inventory" | "development" | "details";

export function getMissingEmbryoFields(
  cycle: Cycle,
  step: EmbryoFormStep,
): string[] {
  if (cycle.type !== "embryos") return [];
  const missing: string[] = [];
  const e = cycle.embryo;
  if (step === "inventory") {
    if (!(e.numberFrozen > 0)) {
      missing.push("cryo_vault.add_modal.embryo.number_frozen");
    }
    // Each embryo's day and status are entered on this step in individual
    // mode, so they are owed here, not on the development step.
    if (
      cycle.embryoEntryMode !== "group" &&
      !e.individualEntries.every((entry) =>
        Boolean(entry.dayOfDevelopment && entry.status),
      )
    ) {
      missing.push("cryo_vault.add_modal.embryo.individual_development_hint");
    }
  } else if (step === "development") {
    if (!(e.freezeDate || e.fertilisationDate)) {
      missing.push("cryo_vault.add_modal.embryo.freeze_date");
    }
    if (cycle.embryoEntryMode === "group") {
      if (!e.dayOfDevelopment) {
        missing.push("cryo_vault.add_modal.embryo.day_of_development");
      }
      if (!e.fertilisationMethod) {
        missing.push("cryo_vault.add_modal.embryo.fertilisation_method");
      }
      if (!e.pgtPerformed) {
        missing.push("cryo_vault.add_modal.embryo.genetic_testing");
      }
    }
  } else if (step === "details") {
    if (!cycle.storageState) {
      missing.push("cryo_vault.add_modal.fields.storage_state_required");
    }
  }
  return missing;
}

/**
 * A specimen intake needs enough biology to create a record. Facility and
 * cycle linkage are additional details that can be added later, but storage
 * state must be present at save time: without "stored" as an explicit value
 * the specimen is written with `status: "processed"` and never appears in
 * the vault — a silent success that was indistinguishable from a lost save.
 * Requiring the answer here is the cheapest way to make the failure visible
 * where she can fix it.
 */
export function isValidSpecimenCycle(cycle: Cycle): boolean {
  const isEmbryo = cycle.type === "embryos";
  const hasDate =
    cycle.type === "eggs"
      ? cycle.oocyte.freezeDate || cycle.oocyte.retrievalDate
      : cycle.type === "sperm"
        ? cycle.sperm.freezeDate || cycle.sperm.collectedAt
        : cycle.embryo.freezeDate || cycle.embryo.fertilisationDate;
  const hasFrozenCount =
    cycle.type === "eggs"
      ? cycle.oocyte.numberFrozen > 0
      : cycle.type === "sperm"
        ? cycle.sperm.numberFrozen > 0
        : cycle.embryo.numberFrozen > 0;
  const hasStorageState = Boolean(cycle.storageState);
  const hasEmbryoDevelopment =
    !isEmbryo ||
    (cycle.embryoEntryMode === "group"
      ? Boolean(
          cycle.embryo.dayOfDevelopment &&
          cycle.embryo.fertilisationMethod &&
          cycle.embryo.pgtPerformed,
        )
      : cycle.embryo.individualEntries.every((entry) =>
          Boolean(entry.dayOfDevelopment && entry.status),
        ));

  return (
    Boolean(hasDate) &&
    hasFrozenCount &&
    hasStorageState &&
    hasEmbryoDevelopment
  );
}
